import * as DateFNS from 'date-fns';
import {
  PricingByPropertyIdFragment,
  PricingByPropertyIdFragmentDoc,
  TravelCurrencyCode,
  TravelPricingsForAccommodationByPropertyIdPayload,
  useAccommodationPagePricingQuery,
} from '@graphql/generated';
import { ApolloClient, ApolloError, makeVar, useApolloClient, useReactiveVar } from '@apollo/client';
import { useEffect, useMemo, useRef } from 'react';
import { addAction, addError } from '@shared/utils/logger';
import { useIsFeatureEnabled } from '@shared/core/featureFlags';
import { useTravelWhiteLabelCurrency } from '../useTravelWhiteLabelCurrency';
import { getPercentageOff, translatedPrice } from '@shared/utils/accommodationPrice';
import { useMinimumPercentageOffToDisplay } from '../useMinimumPercentageOffToDisplay';
import { useApiMetadata } from '../useApiMetadata';
import { usePricingCallSource } from '../usePricingCallSource';
import { readNavigatorLanguage } from '../../utils/getNavigatorLanguage';

const queryStatusVar = makeVar<{
  loading: boolean;
  called: boolean;
  error?: ApolloError;
}>({ loading: false, called: false, error: undefined });

/**
 *
 * @param joyPlaceIds - array of joyPlaceIds to fetch pricing for
 * @param targetDate - optional date (in milliseconds) for check-in and check-out (-1 day and +1 day from event date respectively)
 */
export const usePricingByJoyPlaceIdQuery = (joyPlaceIds: string[], targetDate?: Maybe<number>) => {
  const checkInDate = targetDate ? DateFNS.format(DateFNS.subDays(targetDate, 1), 'yyyy-MM-dd') : null;
  const checkOutDate = targetDate ? DateFNS.format(DateFNS.addDays(targetDate, 1), 'yyyy-MM-dd') : null;
  const currency = useTravelWhiteLabelCurrency();
  const [localeBasedCurrencyEnabled, loadingUseLocaleCurrencyFlag] = useIsFeatureEnabled('localeBasedHotelCurrency');

  const { deviceType, anonymousId, eventId, userCountryCode } = useApiMetadata();
  const callSource = usePricingCallSource();

  const payload: TravelPricingsForAccommodationByPropertyIdPayload | undefined = useMemo(() => {
    if (!targetDate || !checkInDate || !checkOutDate || !joyPlaceIds.length || loadingUseLocaleCurrencyFlag) {
      return undefined;
    }
    if (DateFNS.isBefore(targetDate, new Date())) {
      return undefined;
    }
    return {
      currencyCode: localeBasedCurrencyEnabled ? currency : TravelCurrencyCode.USD,
      dates: {
        checkIn: {
          // validated with skip
          high: checkInDate,
          low: checkInDate,
        },
        // validated with skip
        checkOut: {
          high: checkOutDate,
          low: checkOutDate,
        },
      },
      occupancies: {
        occupancies: [
          {
            adults: 2,
            childrenAges: [],
          },
        ],
        unitCount: 1,
      },
      propertyIdentifiers: joyPlaceIds
        .map(joyPlaceId => ({
          joyPlaceId: joyPlaceId || null,
        }))
        .filter(joyPlaceId => joyPlaceId.joyPlaceId !== null),
      metadata: {
        deviceType,
        anonymousId,
        eventId,
        callSource,
        userCountryCode,
      },
    };
  }, [
    eventId,
    currency,
    deviceType,
    targetDate,
    callSource,
    joyPlaceIds,
    anonymousId,
    checkInDate,
    checkOutDate,
    userCountryCode,
    localeBasedCurrencyEnabled,
    loadingUseLocaleCurrencyFlag,
  ]);

  const { loading, called, error } = useAccommodationPagePricingQuery({
    batchMode: 'fast',
    // skip: !payload prevents the query from being called with undefined payload
    variables: {
      payload: payload!,
    },
    skip: !payload,
    onError(error) {
      addError(error, {
        source: 'Pricing api failed',
        context: 'travelPricingsForAccommodationByPropertyId',
        errorMessage: error.message,
        checkInDate,
        checkOutDate,
        currencyCode: payload?.currencyCode,
        propertyIdentifiers: payload?.propertyIdentifiers,
      });
    },
    onCompleted(data) {
      const pricings = data?.travelPricingsForAccommodationByPropertyId?.pricings;
      if (!pricings?.length) {
        addAction('Pricing api empty results', {
          context: 'travelPricingsForAccommodationByPropertyId',
          checkInDate,
          checkOutDate,
          currencyCode: payload?.currencyCode,
          propertyIdentifiers: payload?.propertyIdentifiers,
        });
      }
    },
  });

  useEffect(() => {
    queryStatusVar({ loading: loading || loadingUseLocaleCurrencyFlag, called, error });
  }, [loading, loadingUseLocaleCurrencyFlag, called, error]);

  return {
    loading: loading || loadingUseLocaleCurrencyFlag,
    called,
    error: error,
  };
};

const readFragmentFromCache = (client: ApolloClient<object>, joyPlaceId: string) => {
  // Define the fragment
  const fragment = PricingByPropertyIdFragmentDoc;

  // Generate the cache ID based on the type and key fields
  const cacheId = `TravelPricingByPropertyIdItem:{"property":{"joyPlaceId":"${joyPlaceId}"}}`;

  // Read the fragment from the cache
  const data = client.cache.readFragment<PricingByPropertyIdFragment>({
    id: cacheId,
    fragment,
  });

  return data;
};

/**
 *
 * @param joyPlaceId - joyPlaceId to fetch pricing for
 * @returns
 */
export const usePricingByJoyPlaceIdFragment = (
  joyPlaceId: Maybe<string>,
  skip?: boolean,
): { price: Maybe<string>; strikeOutPrice: Maybe<string>; percentageOff: Maybe<number>; loading: boolean; error?: Error } => {
  // Cannot use useFragment because we are blocked by Node version <18
  // const { data, complete } = useFragment<PricingByPropertyIdFragment>({
  //   fragment: PricingByPropertyIdFragmentDoc,
  //   fragmentName: 'PricingByPropertyId',
  //   from: {
  //     __typename: 'TravelPricingByPropertyIdItem',
  //     property: {
  //       __typename: 'AccommodationCatalogProperty',
  //       joyPlaceId: joyPlaceId
  //     }
  //   }
  // });
  const complaintRef = useRef<boolean>(false);
  const client = useApolloClient();
  const navigatorLanguage = readNavigatorLanguage();
  const { loading, error } = useReactiveVar(queryStatusVar);
  const { minimumPercentageOffToDisplayValue, loadingMinimumPercentageOffToDisplay } = useMinimumPercentageOffToDisplay();

  if (loading || loadingMinimumPercentageOffToDisplay) {
    return { price: undefined, strikeOutPrice: undefined, percentageOff: undefined, loading: true };
  }
  if (error) {
    return { price: null, strikeOutPrice: undefined, percentageOff: undefined, loading: false, error: error };
  }
  if (skip) {
    return { price: undefined, strikeOutPrice: undefined, percentageOff: undefined, loading: false };
  }
  const data = readFragmentFromCache(client, joyPlaceId!);

  const priceData = data?.pricingData?.perNightFinalAmountBeforeTaxesAndFees;
  const minorUnits = priceData?.valueInMinorUnits;
  const minorUnitsDigits = priceData?.digits;
  const currency = priceData?.currency;
  const price = (joyPlaceId && (minorUnits && currency ? translatedPrice(minorUnits, minorUnitsDigits!, currency, navigatorLanguage) : null)) || undefined;

  const strikeOutPriceData = data?.pricingData?.perNightOriginalAmountBeforeTaxesAndFees;
  const strikeOutMinorUnits = strikeOutPriceData?.valueInMinorUnits;
  const strikeOutMinorUnitsDigits = strikeOutPriceData?.digits;
  const strikeOutCurrency = strikeOutPriceData?.currency;
  const strikeOutPriceValid = strikeOutMinorUnits && minorUnits && strikeOutMinorUnits > minorUnits;

  if (strikeOutMinorUnits && !strikeOutPriceValid && !complaintRef.current) {
    complaintRef.current = true;
    addError(new Error('Strikeout price is not valid'), {
      context: 'usePricingByJoyPlaceIdFragment',
      strikeOutPriceUnits: strikeOutMinorUnits,
      priceUnits: minorUnits,
      joyPlaceId: joyPlaceId,
    });
  }
  const strikeOutPrice =
    strikeOutPriceValid && strikeOutMinorUnits && strikeOutCurrency
      ? translatedPrice(strikeOutMinorUnits, strikeOutMinorUnitsDigits!, strikeOutCurrency, navigatorLanguage)
      : undefined;

  const percentageOff = getPercentageOff(minimumPercentageOffToDisplayValue, minorUnits, strikeOutMinorUnits, minorUnitsDigits, strikeOutMinorUnitsDigits);

  return { price, strikeOutPrice, percentageOff, loading: loading };
};
